import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { v4 as uuidv4 } from 'uuid';

import './Checkbox.scss';

/**
 * Renders a stylized checkbox input with optional text label
 * @example
 * import Checkbox from './Checkbox';
 *
 * const MyComponent = () => {
 *   return (
 *     <Checkbox
 *        name="consent"
 *        checked={this.props.consent}
 *        onChange={this.updateConsent}
 *        label="&#128172; StyleSeat will use this number to text me reminders about bookings"
 *        aria-label="consent-to-use"
 *      />
 *   );
 * }
 *
 */
export default function Checkbox(props) {
  const {
    name,
    label,
    ariaLabel,
    checked,
    defaultChecked,
    onChange,
    className,
    uncheckedAppearance,
    checkedColor,
    testID,
    ignoreClick,
    disabled,
  } = props;
  const id = useMemo(() => `checkbox-${uuidv4()}`, []);

  const checkedProps = {};
  if (checked !== undefined) {
    checkedProps.checked = !!checked;
  } else if (defaultChecked !== undefined) {
    checkedProps.defaultChecked = !!defaultChecked;
  }

  const handleChange = (...args) => {
    if (typeof onChange === 'function') {
      onChange(...args);
    }
  };

  if (ignoreClick && typeof onChange === 'function') {
    // eslint-disable-next-line no-console
    console.warn(`
    Using ignoreClick option on Checkbox will cause onChange to not trigger,
    but you have defined both. Check your use case.
    `);
  }

  return (
    <span
      className={classNames(
        'checkbox-component',
        className,
        checkedColor,
        uncheckedAppearance,
        {
          'no-click': ignoreClick,
        },
      )}
    >
      <input
        type="checkbox"
        id={id}
        name={name}
        onChange={handleChange}
        aria-label={ariaLabel}
        data-testid={testID}
        {...checkedProps}
        disabled={disabled}
      />
      <label
        htmlFor={id}
        style={{ alignItems: 'center' }}
      >
        {label}
      </label>
    </span>
  );
}

Checkbox.propTypes = {
  name: PropTypes.string,
  label: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.node,
  ]),
  ariaLabel: PropTypes.string,
  checked: PropTypes.bool,
  defaultChecked: PropTypes.bool,
  onChange: PropTypes.func,
  className: PropTypes.string,
  uncheckedAppearance: PropTypes.oneOf([
    'outline',
    'solid',
    'hollow',
    'normal',
  ]),
  checkedColor: PropTypes.oneOf([
    'blue',
    'punch',
    'slate',
  ]),
  testID: PropTypes.string,
  ignoreClick: PropTypes.bool,
  disabled: PropTypes.bool,
};

Checkbox.defaultProps = {
  name: '',
  label: '',
  ariaLabel: '',
  checked: undefined,
  defaultChecked: undefined,
  onChange: undefined,
  className: '',
  checkedColor: 'slate',
  uncheckedAppearance: 'outline',
  testID: 'checkbox',
  ignoreClick: false,
  disabled: false,
};
